import {MAX_REGEX_CAPTURE_GROUPS} from '../constants';

type CaptureGroupMetadata = {
  start: number;
  end: number;
  content: string;
};

export function validateMultiMetricQuickAddRegex(pattern: string): {
  errorMessage: string | null;
} {
  // Validate regex syntax
  try {
    new RegExp(pattern);
  } catch (error) {
    return {errorMessage: 'Invalid regex syntax.'};
  }

  // Validate no nested groups
  if (hasNestedGroups(pattern)) {
    return {errorMessage: 'Nested capture groups are not allowed.'};
  }

  // Validate num capture groups
  const captureGroups = extractCaptureGroups(pattern);
  if (captureGroups.length > MAX_REGEX_CAPTURE_GROUPS) {
    return {
      errorMessage: `Up to ${MAX_REGEX_CAPTURE_GROUPS} capture groups are allowed.`,
    };
  }

  // Validate no empty capture groups
  if (captureGroups.some(group => group.content === '')) {
    return {errorMessage: 'Empty capture groups are not allowed.'};
  }

  return {errorMessage: null};
}

/**
 * Given a metric and a regex pattern, return regex with captured values filled in.
 * Input regex pattern is expected to be pre-validated for multi-metric quick add.
 * Returns undefined if the metric does not match the regex.
 * @example
 *   getFilledRegex("foo/bar", "foo/(.+)") -> "foo/(bar)"
 *   getFilledRegex("foo/bar", "baz/bar") -> undefined
 */
export function getFilledRegex(
  metric: string,
  validatedPattern: string
): string | undefined {
  const regex = new RegExp(validatedPattern);
  const match = regex.exec(metric);

  if (!match) {
    return;
  }

  // Extract capture groups
  const captureGroups = extractCaptureGroups(validatedPattern);

  // If no capture groups, return pattern as-is
  if (captureGroups.length === 0) {
    return validatedPattern;
  }

  // Replace capture groups with captured values from right to left (preserves indices)
  let filledPattern = validatedPattern;
  for (let i = captureGroups.length - 1; i >= 0; i--) {
    const group = captureGroups[i];
    const capturedValue = match[i + 1];
    if (capturedValue !== undefined) {
      const before = filledPattern.substring(0, group.start);
      const after = filledPattern.substring(group.end);
      // Build fill value by replacing regex patterns while preserving escaped literals
      const groupContent = group.content;
      const fillValue = buildFilledPattern(groupContent, capturedValue);
      filledPattern = `${before}(${fillValue})${after}`;
    }
  }
  return filledPattern;
}

/**
 * Builds a filled pattern by replacing regex metacharacters with
 * captured values while preserving escaped literal characters.
 * Assumes capturedValue is already validated to match the pattern.
 * @example
 *   buildFilledPattern("\\w+\\.bar", "foo.bar") -> "foo\\.bar"
 *   buildFilledPattern("\\.baz", ".baz") -> "\\.baz"
 *   buildFilledPattern(".+", "foo") -> "foo"
 */
function buildFilledPattern(pattern: string, capturedValue: string): string {
  // Verify that captured value matches the pattern
  const regex = new RegExp(`^${pattern}$`);
  if (!regex.test(capturedValue)) {
    // If pattern doesn't match, return captured value as-is.
    // This shouldn't happen if the parent function processed the
    // inputs correctly, but adding a defensive check to be safe.
    return capturedValue;
  }

  // Extract escaped literals from pattern
  const escapedLiterals: string[] = [];
  for (let i = 0; i < pattern.length; i++) {
    if (pattern[i] === '\\' && i + 1 < pattern.length) {
      const nextChar = pattern[i + 1];
      // Check if this is an escaped literal character (these characters have
      // special meaning in regex and need to be escaped to be used literally)
      const isEscapedLiteral = /[.*+?^${}()|[\]\\/-]/.test(nextChar);
      if (isEscapedLiteral) {
        escapedLiterals.push(pattern[i] + nextChar);
      }
      i++; // Skip next char
    }
  }

  // If pattern contains no escaped literals, return capturedValue as-is
  if (escapedLiterals.length === 0) {
    return capturedValue;
  }

  // If pattern contains only escaped literals, return pattern as-is
  if (escapedLiterals.join('') === pattern) {
    return pattern;
  }

  // If pattern mixes escaped literals and regex metacharacters,
  // we need to build the result by finding the escaped literals
  // in the capturedValue and preserving the escapes.

  let result = ''; // the resulting pattern that will be returned
  let valueIdx = 0; // where in capturedValue we have processed up to

  for (const escapedLiteral of escapedLiterals) {
    // escapedLiteral includes the backslash, so index 1 is the literal char
    const literalChar = escapedLiteral[1];
    const literalCharIdx = capturedValue.indexOf(literalChar, valueIdx);
    // Add everything before the literal
    result += capturedValue.substring(valueIdx, literalCharIdx);
    // Add the escaped version
    result += escapedLiteral;
    // Move past the literal
    valueIdx = literalCharIdx + 1;
  }

  // Append remaining captured value
  result += capturedValue.substring(valueIdx);

  return result;
}

/**
 * Checks whether a regex pattern contains nested groups.
 * @example
 *   hasNestedGroups("foo/(b(ar))/baz") -> true
 *   hasNestedGroups("foo/(bar)/baz") -> false
 */
export function hasNestedGroups(pattern: string): boolean {
  let isInGroup = false;

  for (let i = 0; i < pattern.length; i++) {
    const char = pattern[i];

    // Ignore escaped characters
    if (char === '\\') {
      i++; // skips the next character, which is escaped
      continue;
    }

    // Check for start of group
    if (char === '(') {
      // Ignore non-capturing groups
      const isNonCapturing = pattern[i + 1] === '?' && pattern[i + 2] === ':';
      if (isNonCapturing) {
        i += 2; // skip next two chars (?:)
        continue;
      }

      // Opening paren while already in a group -> nested group found
      if (isInGroup) {
        return true;
      }

      isInGroup = true;
    } else if (isInGroup && char === ')') {
      isInGroup = false;
    }
  }

  return false;
}

/**
 * Extracts capture group positions from a regex pattern.
 * Assumes no nested groups.
 * @example
 *   extractCaptureGroups("foo/(.+)/(baz)") => [
 *     {start: 4, end: 8, content: ".+"},
 *     {start: 9, end: 14, content: "baz"},
 *   ]
 */
export function extractCaptureGroups(pattern: string): CaptureGroupMetadata[] {
  const groups: CaptureGroupMetadata[] = [];
  let isInCharClass = false;

  for (let i = 0; i < pattern.length; i++) {
    const char = pattern[i];

    // Ignore escaped characters
    if (char === '\\') {
      i++;
      continue;
    }

    // Track character classes (e.g. [a-z()], [^a-z()])
    if (char === '[') {
      isInCharClass = true;
      if (pattern[i + 1] === '^') {
        // A ^ immediately after a [ indicates a negated character class
        // We should skip this next character
        i++;
      }
      if (pattern[i + 1] === ']') {
        // A ] at the start of a character class (or negated character class)
        // should be treated as a literal ], even if it's not escaped.
        // We should skip this character as well.
        i++;
      }
      continue;
    } else if (char === ']') {
      isInCharClass = false;
      continue;
    }

    // Ignore anything that's not the start of a group
    // Note: parens inside character classes are not capture groups
    if (char !== '(' || isInCharClass) {
      continue;
    }

    if (char === '(') {
      // Ignore non-capturing groups
      const isNonCapturing = pattern[i + 1] === '?' && pattern[i + 2] === ':';
      if (isNonCapturing) {
        i += 2; // skip next two chars (?:)
        continue;
      }

      // Find closing paren
      let j = i + 1;
      let isInCharClassInner = false;
      while (j < pattern.length) {
        const innerChar = pattern[j];

        // Ignore escaped characters
        if (innerChar === '\\') {
          j += 2;
          continue;
        }

        // Track character classes inside the group
        if (innerChar === '[') {
          isInCharClassInner = true;
          // Handle ] as literal at start of character class
          if (pattern[j + 1] === '^') {
            j++;
          }
          if (pattern[j + 1] === ']') {
            j++;
          }
        } else if (innerChar === ']') {
          isInCharClassInner = false;
        }

        // Stop when we find the closing paren (outside character classes)
        if (!isInCharClassInner && innerChar === ')') {
          break;
        }

        j++;
      }

      // Add capture group
      const content = pattern.substring(i + 1, j);
      groups.push({start: i, end: j + 1, content});

      // Continue scanning from the end of the group
      i = j;
    }
  }

  return groups;
}
